import { useEffect, useState } from 'react';
import { IconLine, IconRectangle, IconOval } from '@tabler/icons-react';
import { ChipButton, PanelHeader, PanelHint, PanelTitle, SectionLabel, Segmented, SegmentButton, Swatch } from './ui/Panel';

type SHAPE_TYPE = 'line' | 'rect' | 'ellipse';

const SHAPES: { type: SHAPE_TYPE; label: string; Icon: any }[] = [
	{ type: 'line', label: 'Line', Icon: IconLine },
	{ type: 'rect', label: 'Rectangle', Icon: IconRectangle },
	{ type: 'ellipse', label: 'Ellipse', Icon: IconOval },
];

export type ToolsShapesProps = {
	p5: () => any; // p5 instance
	/** Shape colour and thickness are shared with the pencil */
	pencilHex: string;
	pencilSize: { x: number; y: number };
	onEditColour: () => void;
	onEditSize: () => void;
};

function ToolsShapes({ p5, pencilHex, pencilSize, onEditColour, onEditSize }: ToolsShapesProps) {
	const [shapeType, setShapeType] = useState<SHAPE_TYPE>('line');
	const [filled, setFilled] = useState(false);
	const [snap, setSnap] = useState(false);

	useEffect(() => {
		if (typeof p5()?.getShapeType === 'function') setShapeType(p5().getShapeType());
		if (typeof p5()?.getShapeFilled === 'function') setFilled(!!p5().getShapeFilled());
		if (typeof p5()?.getShapeSnap === 'function') setSnap(!!p5().getShapeSnap());
	}, [p5]);

	const selectShape = (type: SHAPE_TYPE) => {
		setShapeType(type);
		p5().setShapeType(type);
	};

	const selectFilled = (isFilled: boolean) => {
		setFilled(isFilled);
		p5().setShapeFilled(isFilled);
	};

	const selectSnap = (isSnap: boolean) => {
		setSnap(isSnap);
		p5().setShapeSnap(isSnap);
	};

	return (
		<div style={{ marginTop: 18 }}>
			<PanelHeader>
				<div>
					<PanelTitle>Draw a shape</PanelTitle>
					<PanelHint>Drag on the canvas from start to end</PanelHint>
				</div>
				<div style={{ display: 'flex', gap: 6 }}>
					<ChipButton type="button" onClick={onEditColour}>
						<Swatch $color={pencilHex} />
						Colour
					</ChipButton>
					<ChipButton type="button" onClick={onEditSize} aria-label="Line thickness">
						{pencilSize.x} x {pencilSize.y}
					</ChipButton>
				</div>
			</PanelHeader>

			<SectionLabel>Shape</SectionLabel>
			<Segmented>
				{SHAPES.map(({ type, label, Icon }) => (
					<SegmentButton key={type} type="button" active={shapeType === type} onClick={() => selectShape(type)}>
						<Icon size={16} />
						{label}
					</SegmentButton>
				))}
			</Segmented>

			{shapeType === 'line' ? (
				<>
					<SectionLabel>Angle</SectionLabel>
					<Segmented>
						<SegmentButton type="button" active={!snap} onClick={() => selectSnap(false)}>
							Free
						</SegmentButton>
						<SegmentButton type="button" active={snap} onClick={() => selectSnap(true)}>
							Straighten (45°)
						</SegmentButton>
					</Segmented>
				</>
			) : (
				<>
					<SectionLabel>Style</SectionLabel>
					<Segmented>
						<SegmentButton type="button" active={!filled} onClick={() => selectFilled(false)}>
							Outline
						</SegmentButton>
						<SegmentButton type="button" active={filled} onClick={() => selectFilled(true)}>
							Filled
						</SegmentButton>
					</Segmented>
				</>
			)}
		</div>
	);
}

export default ToolsShapes;
